import s from './ActionsCell.module.scss';
import { getAppEnvironment } from '@/shared/lib/runtimeEnvironment';
import { CellContext } from '@tanstack/react-table';
import { DeleteAccountButton } from '@/features/DeleteAccount';
import { RestoreAccountButton } from '@/features/RestoreAccount';
import { IconButton } from '@/shared/ui/IconButton/IconButton';
import HistoryIcon from '@/shared/assets/icons/history.svg';
import ServerIcon from '@/shared/assets/icons/server.svg';
import ProfileIcon from '@/shared/assets/icons/profile.svg';
import type { MouseEvent } from 'react';
import {
  canRestoreAccount,
  getAccountDisplayName,
  getClubDisplayName,
  isActiveAccount,
} from '@/shared/lib/utils/accountPresentation';
import type { AccountDeletionSummary } from '@/shared/model/types/accountDeletion';
import { AccountStatusCell } from '@/shared/ui/AccountStatusCell/AccountStatusCell';

interface ActionsCellProps<T> {
  row: CellContext<T, unknown>;
  onOpenLog: (userId: number, userName: string) => void;
  onOpenServers?: (userId: number, userName: string) => void;
  showHistory?: boolean;
  showServers?: boolean;
  onDelete?: (id: number, name: string) => void;
  onOpenAccounts?: (userId: number, userName: string, readOnly?: boolean) => void;
  deleteMode?: 'account' | 'club';
}

export const ActionsCell = <T,>(props: ActionsCellProps<T>) => {
  const {
    row,
    onOpenLog,
    onOpenServers,
    showHistory = true,
    showServers = true,
    onDelete,
    onOpenAccounts,
    deleteMode = 'account',
  } = props;

  const original = row.row.original as {
    id: number;
    name?: string;
    nickname?: string;
    lastName?: string;
    firstName?: string;
    patronymic?: string | null;
    isChanged?: boolean;
    isActive?: boolean;
    accountStatus?: 'active' | 'pending_deletion' | 'deleted' | null;
    deletion?: AccountDeletionSummary | null;
  };
  const isClubRow = deleteMode === 'club' || Boolean(onOpenAccounts);
  const displayName = isClubRow ? getClubDisplayName(original) : getAccountDisplayName(original);
  const isChanged = Boolean(original.isChanged);
  const isDeleted = original.accountStatus === 'deleted';
  const isClubReadOnly = isClubRow && !isActiveAccount(original.accountStatus);
  const isDeletionLocked = isClubRow ? isClubReadOnly : original.accountStatus === 'pending_deletion' || isDeleted;

  const onHistoryClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onOpenLog(original.id, displayName);
  };

  const onServersClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onOpenServers) {
      onOpenServers(original.id, displayName);
    }
  };

  const onAccountsClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (onOpenAccounts) {
      onOpenAccounts(original.id, displayName, isClubReadOnly);
    }
  };

  return (
    <div className={s.ActionsCell}>
      {onOpenAccounts && (
        <span className={s.historyWrapper} title="Аккаунты клуба">
          <IconButton size="m" onClick={onAccountsClick}>
            <ProfileIcon />
          </IconButton>
        </span>
      )}
      {showServers && onOpenServers && (
        <span className={s.historyWrapper} title="История серверов">
          <IconButton size="m" onClick={onServersClick}>
            <ServerIcon />
          </IconButton>
        </span>
      )}
      {showHistory && (
        <span className={s.historyWrapper} title="История изменений">
          <IconButton size="m" onClick={onHistoryClick}>
            <HistoryIcon className={isChanged ? s.historyChanged : s.historyDefault} />
          </IconButton>
        </span>
      )}

      {original.accountStatus === 'pending_deletion' ? (
        <>
          <AccountStatusCell
            status={original.accountStatus}
            isActive={original.isActive ?? false}
            deletion={original.deletion}
            variant="remaining"
          />
          {canRestoreAccount(original.accountStatus, original.deletion) && (
            <RestoreAccountButton userId={original.id} userName={displayName} mode={deleteMode} />
          )}
        </>
      ) : isDeletionLocked ? null : (
        <DeleteAccountButton
          arrayOfName={[displayName]}
          arrayOfId={[original.id]}
          isIcon
          mode={deleteMode as 'account' | 'club'}
          accountStatuses={[original.accountStatus]}
        />
      )}
      {deleteMode === 'account' && getAppEnvironment() === 'dev' && !isDeletionLocked && (
        <DeleteAccountButton
          arrayOfName={[displayName]}
          arrayOfId={[original.id]}
          isIcon
          mode="account"
          deletionType="hard"
          accountStatuses={[original.accountStatus]}
        />
      )}
    </div>
  );
};
